import React from 'react';

const API_BASE = 'https://ndb4d1r8u9.execute-api.us-east-1.amazonaws.com/dev';

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=Nunito:wght@400;500;600&display=swap');

* { margin: 0; padding: 0; box-sizing: border-box; }

.pb-page {
  min-height: 100svh;
  background: #faf8f5;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 24px;
  font-family: 'Lora', Georgia, serif;
  color: #0f172a;
}

.pb-card {
  width: 100%; max-width: 480px;
  background: #ffffff;
  border: 1px solid rgba(15,23,42,0.1);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(15,23,42,0.08);
}

.pb-photo {
  width: 100%; aspect-ratio: 4/3;
  object-fit: cover;
  display: block;
  background: rgba(15,23,42,0.05);
}

.pb-body {
  padding: 32px 28px 36px;
  text-align: center;
}

.pb-label {
  font-family: 'Nunito', sans-serif;
  font-size: 0.85rem;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #3c4653;
  margin-bottom: 12px;
}

.pb-title {
  font-family: 'Lora', serif;
  font-size: 1.5rem;
  font-weight: 500;
  color: #0f172a;
  margin-bottom: 8px;
  line-height: 1.4;
}

.pb-from {
  font-family: 'Lora', serif;
  font-style: italic;
  font-size: 1.1rem;
  color: #3c4653;
  margin-bottom: 28px;
}

.pb-divider {
  display: flex; align-items: center; gap: 12px;
  justify-content: center; margin: 0 0 24px;
  color: rgba(15,23,42,0.15);
}
.pb-divider::before, .pb-divider::after {
  content: ''; flex: 1; max-width: 60px; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(15,23,42,0.12), transparent);
}

.pb-player {
  width: 100%;
  margin-bottom: 8px;
}

.pb-player audio {
  width: 100%;
  border-radius: 8px;
}

.pb-player video {
  width: 100%;
  border-radius: 12px;
  background: #000;
  max-height: 60vh;
}

.pb-brand {
  text-align: center;
  margin-top: 28px;
  font-family: 'Lora', serif;
  font-size: 0.85rem;
  letter-spacing: 3px;
  color: #c8bda8;
}

.pb-loading {
  text-align: center;
  padding: 60px 24px;
}

.pb-spinner {
  width: 32px; height: 32px;
  border: 2px solid rgba(15,23,42,0.1);
  border-top-color: #3c4653;
  border-radius: 50%;
  animation: pb-spin 0.8s linear infinite;
  margin: 0 auto 16px;
}

@keyframes pb-spin { to { transform: rotate(360deg); } }

.pb-error {
  text-align: center;
  padding: 60px 24px;
}

.pb-error-icon {
  font-size: 2.5rem;
  margin-bottom: 16px;
}

.pb-error-text {
  font-family: 'Lora', serif;
  font-size: 1.15rem;
  color: #3c4653;
  line-height: 1.6;
}

@media (max-width: 480px) {
  .pb-page { padding: 16px; }
  .pb-body { padding: 24px 20px 28px; }
  .pb-title { font-size: 1.3rem; }
}
`;

export default function PlaybackPage({ shareId }) {
  const [media, setMedia] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    // Prevent search engines from indexing private playback URLs
    if (!document.querySelector('meta[name="robots"]')) {
      const meta = document.createElement('meta');
      meta.name = 'robots';
      meta.content = 'noindex, nofollow';
      document.head.appendChild(meta);
    }
    if (!shareId) { setError('Invalid link.'); setLoading(false); return; }

    fetch(`${API_BASE}/media/${shareId}`)
      .then(res => {
        if (!res.ok) throw new Error(res.status === 404 ? 'This recording could not be found.' : 'Something went wrong.');
        return res.json();
      })
      .then(data => {
        if (data.error) throw new Error(data.error);
        setMedia(data);
      })
      .catch(err => { console.error('Playback load error:', err); setError('We had trouble loading this recording. Please try again.'); })
      .finally(() => setLoading(false));
  }, [shareId]);

  return (
    <div className="pb-page">
      <style>{STYLES}</style>

      {loading && (
        <div className="pb-loading">
          <div className="pb-spinner" />
          <div style={{ fontFamily: "'Lora', serif", color: '#3c4653', fontStyle: 'italic' }}>Loading...</div>
        </div>
      )}

      {error && (
        <div className="pb-error">
          <div className="pb-error-icon">{'\u{1F4D6}'}</div>
          <div className="pb-error-text">{error}</div>
          <div className="pb-brand">Talebound</div>
        </div>
      )}

      {media && !error && (
        <div className="pb-card">
          {media.photoUrl && (
            <img className="pb-photo" src={media.photoUrl} alt={media.title || 'Chapter photo'} />
          )}
          <div className="pb-body">
            <p className="pb-label">{media.mediaType === 'VIDEO' ? 'A video message' : 'A recorded message'}</p>
            {media.title && <h1 className="pb-title">{media.title}</h1>}
            {media.storytellerName && (/\s/.test(media.storytellerName) || /[A-Z]/.test(media.storytellerName)) && <p className="pb-from">from {media.storytellerName}</p>}

            <div className="pb-divider">{'\u2726'}</div>

            <div className="pb-player">
              {media.mediaType === 'VIDEO' ? (
                <video controls preload="metadata" playsInline src={media.mediaUrl}>
                  Your browser does not support video playback.
                </video>
              ) : (
                <audio controls preload="metadata" src={media.mediaUrl}>
                  Your browser does not support audio playback.
                </audio>
              )}
            </div>
          </div>
          <div className="pb-brand">Talebound</div>
          <div style={{ height: 20 }} />
        </div>
      )}
    </div>
  );
}
